import React from 'react';
import Grid from '@material-ui/core/Grid';
import TableCell from '@material-ui/core/TableCell';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import SearchIcon from '@material-ui/icons/Search';

import MultiUseDialog from '../Components/MultiUseDialog';

import Collection from '../Model/Collection';

import ItemTableRow from '../Components/ItemTableRow';
import ProjectActivityEventCollectionFields from '../Model/ProjectActivityEventCollectionFields';

import {
  GetProjectActivityEventsPathForApi,
} from '../Util/api';
import {
  GetDateValue,
  GetUserValue,
} from '../Util/Properties';
import {
  OpenDocumentDetailTab,
} from '../Util/Documents';
import {
  HandleRouteToTask,
} from '../Util/Tasks';
import {
  HandleRouteToAssetItem,
} from '../Util/AssetItems';

const getSourceTypeLabel = (sourceType) => {
  switch (sourceType) {
    case "Document":
      return "Document";
    case "Task":
      return "Task";
    case "AssetItem":
      return "Asset Item";
    default:
      return sourceType || "";
  }
};

const getEventTypeLabel = (sourceType, sourceEventType) => {
  switch (sourceEventType) {
    case "Revision":
      return (sourceType === "Task") ? "Updated" : "Revision";
    case "Append":
      return "Pages Added";
    case "NameChange":
      return "Name Changed";
    case "FieldsCommit":
      return "Fields Updated";
    case "TagsChange":
      return "Tags Changed";
    case "Comment":
      return "Comment";
    case "DocumentApprovalAction":
    case "ApprovalAction":
      return "Approval Action";
    case "GroupApprovalAction":
      return "Group Approval Action";
    case "RecycleBinAction":
      return "Recycle Bin";
    case "DocumentFolderChange":
      return "Folder Changed";
    case "SignatureSessionEvent":
      return "Signature";
    case "FormSubmissionEvent":
      return "Form Submission";
    case "Complete":
      return "Completed";
    case "Delete":
      return "Deleted";
    case "AssignmentChange":
      return "Assignment Changed";
    case "Creation":
      return "Created";
    case "Archive":
      return "Archived";
    case "Restore":
      return "Restored";
    default:
      return sourceEventType || "";
  }
};

const getEventDetail = (event) => {
  if (!event.DetailJson) {
    return "";
  }
  try {
    const detail = JSON.parse(event.DetailJson);
    switch (event.SourceEventType) {
      case "Revision":
        if (event.SourceType === "Document") {
          return (detail.Revision === 0) ? "Creation" : `Revision ${detail.Revision}`;
        }
        return "";
      case "Append":
        return `${detail.PagesAdded} page${(detail.PagesAdded > 1) ? "s" : ""} added`;
      case "NameChange":
        return `'${detail.OldName}' → '${detail.NewName}'`;
      case "FieldsCommit": {
        const fields = [];
        for (const key of Object.keys(detail.FieldNamesAndValues || {})) {
          fields.push(`${key}: ${detail.FieldNamesAndValues[key].join(", ")}`);
        }
        return fields.join("; ");
      }
      case "TagsChange": {
        const tags = [...(detail.Tags || [])];
        (detail.AssetItemTags || []).forEach(ait => {
          tags.push(`${ait.AssetItemName} (${ait.AssetName})`);
        });
        return tags.join(", ");
      }
      case "Comment":
        return detail.Comment
          ? (detail.Comment.length > 120 ? detail.Comment.substring(0, 120) + "..." : detail.Comment)
          : "";
      case "DocumentApprovalAction":
      case "ApprovalAction": {
        const actions = { Start: "Started", Approve: "Approved", Decline: "Declined", Deny: "Denied" };
        const action = actions[detail.Action] || detail.Action;
        return `${action} for ${detail.ProcessName}, ${detail.ProcessElementName}`;
      }
      case "GroupApprovalAction": {
        const actions = { Start: "Started", Approve: "Approved", Decline: "Declined", Deny: "Denied" };
        const action = actions[detail.Action] || detail.Action;
        const users = (detail.AssignmentUsers || []).map(au => au.UserEmail).join(", ");
        return `${action} for ${detail.ProcessName}, ${detail.ProcessElementName}; ${users}`;
      }
      case "RecycleBinAction":
        return (detail.Action === "SendTo") ? "Sent to Recycle Bin" : "Restored from Recycle Bin";
      case "DocumentFolderChange":
        return detail.DocumentFolderPath || "No folder";
      case "SignatureSessionEvent":
        switch (detail.DocumentSignatureSessionEventType) {
          case "SessionStart": {
            const signers = (detail.SignerNamesAndEmails || []).map(s => `${s[0]} (${s[1]})`).join(", ");
            return `Initiated; Signers: ${signers}`;
          }
          case "RecipientSigning":
            return "Signature received";
          case "SessionStop":
            return "Session stopped";
          case "SessionCompletion":
            return "Signature-gathering complete";
          case "RenderCompletion":
            return "Signatures rendered";
          default:
            return "";
        }
      case "FormSubmissionEvent":
        return (detail.FormSubmissionEventType === "Submission") ? "Form submitted" : "Form rendered to PDF";
      case "Complete":
        return "Task completed";
      case "Delete":
        return "Task deleted";
      case "AssignmentChange":
        return `${detail.OriginalAssignmentUserName || detail.OriginalAssignmentUserEmail || "(unassigned)"} → ${detail.NewAssignmentUserName || detail.NewAssignmentUserEmail || "(unassigned)"}`;
      case "Creation":
        return "Item created";
      case "Archive":
        return "Item archived";
      case "Restore":
        return "Item restored";
      default:
        return "";
    }
  } catch (e) {
    return "";
  }
};

const getEventSummary = (event) => {
  const typeLabel = getEventTypeLabel(event.SourceType, event.SourceEventType);
  const sourceLabel = getSourceTypeLabel(event.SourceType);
  const name = event.SourceName || "(unnamed)";
  const parentPrefix = (event.SourceParentName)
    ? `${event.SourceParentName} > `
    : "";
  return `${sourceLabel} ${typeLabel}: ${parentPrefix}${name}`;
};

export class ProjectActivityEvents_Collection extends Collection {
  constructor(props, onSetState, onApiError, onAlert, organizationId, projectId) {

    if (typeof organizationId !== "string") {
      console.log("Warning: organizationId required and not found");
    }
    if (typeof projectId !== "string") {
      console.log("Warning: projectId required and not found");
    }
    super(props, onSetState, onApiError, false, true, organizationId, projectId);

    this.PageTitle = "Events";
    this.ContentUri = GetProjectActivityEventsPathForApi(organizationId, projectId);
    this.ContentUriParams = {};
    this.CollectionName = "ProjectActivityEvents";
    this.ItemsName = "ProjectActivityEvents";
    this.ItemName = "Event";
    this.DefaultViewType = "List";
    this.AllowedViewTypes = ["List"];
    this.DefaultSortDescending = true;
    this.HideFilters = true;

  }

  handleNavigateToSource = (event) => {
    if (!event || !event.SourceID) {
      return;
    }
    switch (event.SourceType) {
      case "Document":
        OpenDocumentDetailTab(this.OrganizationID, this.ProjectID, event.SourceID);
        break;
      case "Task":
        HandleRouteToTask(this.props, this.ProjectID, event.SourceID, true, false, !!event.SourceIsCompleted, !!event.SourceIsDenied);
        break;
      case "AssetItem":
        if (event.SourceParentID) {
          HandleRouteToAssetItem(this.props, this.ProjectID, event.SourceParentID, event.SourceID, !!event.SourceIsArchived);
        }
        break;
      default:
        break;
    }
  }

  handleSourceNameFilterApply = (value) => {
    const filterValue = (value || "").trim();
    this.ContentUriParams = {
      ...this.ContentUriParams,
      sourceNameFilter: filterValue,
    };
    this.setState({
      SourceNameFilterDialogIsOpen: false,
      CollectionUpdateID: new Date(),
    });
  }

  HandleGetAdditionalToolHeaderRightContent = () => {
    const isActive = this.ContentUriParams && this.ContentUriParams.sourceNameFilter;
    return (
      <div style={{position: "relative"}}>
        {isActive && (
          <div style={{
            backgroundColor: "#f44336",
            borderRadius: 4,
            width: 8,
            height: 8,
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 1,
          }} />
        )}
        <Tooltip title="Filter by source name">
          <IconButton
            onClick={() => this.setState({ SourceNameFilterDialogIsOpen: true })}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  HandleGetDialogContent = state => {
    if (state.SourceNameFilterDialogIsOpen) {
      return (
        <MultiUseDialog
          Details={{
            Open: state.SourceNameFilterDialogIsOpen,
            Title: "Filter by source name",
            RequireTextInput1: true,
            TextInput1Label: "Source name",
            TextInput1PlaceHolder: "Start typing a document, task, or asset item name...",
            TextInput1DefaultValue: (this.ContentUriParams && this.ContentUriParams.sourceNameFilter) || "",
            ConfirmLabel: "APPLY",
            ConfirmCallback: value => { this.handleSourceNameFilterApply(value); return true; },
            CancelCallback: () => this.setState({ SourceNameFilterDialogIsOpen: false }),
            CloseCallback: () => this.setState({ SourceNameFilterDialogIsOpen: false }),
          }}
        />
      );
    }
    return null;
  }

  HandleGetCollectionFieldsPromise = () => Promise.resolve(ProjectActivityEventCollectionFields);

  HandleGetHeadCells = (items, sensitiveFields) => [
    { id: 'CreatedOn', sortId: 'CreatedOn', numeric: false, label: 'Date' },
    { id: 'SourceType', sortId: 'SourceType', numeric: false, label: 'Category' },
    { id: 'SourceName', sortId: 'SourceName', numeric: false, label: 'Source' },
    { id: 'CreatedByUserName', sortId: 'CreatedByUserName', numeric: false, label: 'User' },
    { id: 'SourceEventType', sortId: 'SourceEventType', numeric: false, label: 'Event Type' },
    { id: 'Details', numeric: false, label: 'Details' },
  ];

  HandleGetCardGridItems = (items, sensitiveFields, classes, theme, onSelect, selectedIDs, onAction, postActionData, sortType, sortDescending) => {
    return (items && items.length)
      ? items.map(i => {
        return (
          <Grid item key={i.ID} sm={12} md={6} lg={4} xl={3} className={classes.cardGridItem}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">
                  {GetDateValue(i.CreatedOn)}
                </Typography>
                <Typography variant="body1">
                  {i.SourceID ? (
                    <a
                      href="#"
                      onClick={e => { e.preventDefault(); this.handleNavigateToSource(i); }}
                      style={{
                        color: theme.palette.primary.main,
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      {getEventSummary(i)}
                    </a>
                  ) : getEventSummary(i)}
                </Typography>
                {GetUserValue(i.CreatedByUserEmail, i.CreatedByUserName)}
                {getEventDetail(i) && (
                  <Typography variant="body2" style={{marginTop: 4}}>
                    {getEventDetail(i)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })
      : [];
  }

  HandleGetTableRows = (headCells, items, sensitiveFields, classes, theme, onSelect, selectedIDs, onAction, postActionData, sortType, sortDescending) => {
    return (items && items.length)
      ? items.map(i => {
        const sourceName = (i.SourceParentName)
          ? `${i.SourceParentName} > ${i.SourceName}`
          : i.SourceName;
        return (
          <ItemTableRow key={`k_${i.ID}`}>
            <TableCell className={classes.tableCell_FirstCell} padding="checkbox" />
            <TableCell className={classes.tableCell} component="th" scope="row" padding="none" style={{whiteSpace:"nowrap"}}>
              {GetDateValue(i.CreatedOn)}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {getSourceTypeLabel(i.SourceType)}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {i.SourceID ? (
                <a
                  href="#"
                  onClick={e => { e.preventDefault(); this.handleNavigateToSource(i); }}
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                  onMouseEnter={e => e.target.style.textDecoration = "underline"}
                  onMouseLeave={e => e.target.style.textDecoration = "none"}
                >
                  {sourceName}
                </a>
              ) : sourceName}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {GetUserValue(i.CreatedByUserEmail, i.CreatedByUserName)}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {getEventTypeLabel(i.SourceType, i.SourceEventType)}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {getEventDetail(i)}
            </TableCell>
          </ItemTableRow>
        );
      })
      : [];
  }
}
